if (!customElements.get('video-shopping')) {
  customElements.define('video-shopping', class VideoShopping extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.videos = Array.from(this.querySelectorAll('.video-shopping__video'));
      this.scroller = this.querySelector('.video-shopping__scroller');
      this.previousButton = this.querySelector('.video-shopping__arrow--previous');
      this.nextButton = this.querySelector('.video-shopping__arrow--next');
      this.scrollbar = this.querySelector('.video-shopping__scrollbar');
      this.scrollbarThumb = this.querySelector('.video-shopping__scrollbar-thumb');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.handleScroll = this.handleScroll.bind(this);
      this.scroller?.addEventListener('scroll', this.handleScroll, { passive: true });
      this.videos.forEach((video) => {
        video.addEventListener('play', () => this.onPlay(video));
        video.addEventListener('pause', () => this.onPause(video));
        video.addEventListener('ended', () => this.onPause(video));
      });
      this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), { threshold: 0.6 });
      this.videos.forEach((video) => this.observer.observe(video));
      this.resizeObserver = new ResizeObserver(() => this.updateNavigation());
      if (this.scroller) this.resizeObserver.observe(this.scroller);
      requestAnimationFrame(() => this.updateNavigation());
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.scroller?.removeEventListener('scroll', this.handleScroll);
      this.videos?.forEach((video) => video.pause());
      this.initialized = false;
    }

    handleClick(event) {
      const arrow = event.target.closest('.video-shopping__arrow');
      if (arrow) {
        this.scrollCarousel(arrow.classList.contains('video-shopping__arrow--next') ? 1 : -1);
        return;
      }
      const playButton = event.target.closest('.video-shopping__play');
      if (playButton) {
        this.togglePlayback(playButton.closest('.video-shopping__media'));
        return;
      }
      const volumeButton = event.target.closest('.video-shopping__volume');
      if (volumeButton) {
        this.toggleVolume(volumeButton.closest('.video-shopping__media'));
        return;
      }
      const media = event.target.closest('.video-shopping__media');
      if (media && (event.target.matches('.video-shopping__poster') || event.target.matches('.video-shopping__video'))) {
        this.togglePlayback(media);
      }
    }

    handleScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateNavigation();
      });
    }

    scrollCarousel(direction) {
      if (!this.scroller) return;
      const card = this.querySelector('.video-shopping__card');
      const list = this.querySelector('.video-shopping__list');
      const gap = list ? parseFloat(getComputedStyle(list).columnGap || getComputedStyle(list).gap) || 0 : 0;
      const distance = card ? card.getBoundingClientRect().width + gap : this.scroller.clientWidth * .8;
      this.scroller.scrollBy({ left: direction * distance, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    updateNavigation() {
      if (!this.scroller) return;
      const maxScroll = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
      const position = Math.min(maxScroll, Math.max(0, this.scroller.scrollLeft));
      const tolerance = 2;
      if (this.previousButton) this.previousButton.disabled = position <= tolerance;
      if (this.nextButton) this.nextButton.disabled = position >= maxScroll - tolerance;
      if (!this.scrollbar || !this.scrollbarThumb) return;
      const trackWidth = this.scrollbar.clientWidth;
      const thumbWidth = maxScroll > 0 ? Math.max(18, trackWidth * this.scroller.clientWidth / this.scroller.scrollWidth) : trackWidth;
      const thumbTravel = Math.max(0, trackWidth - thumbWidth);
      const progress = maxScroll > 0 ? position / maxScroll : 0;
      this.scrollbarThumb.style.width = `${thumbWidth}px`;
      this.scrollbarThumb.style.transform = `translateX(${thumbTravel * progress}px)`;
    }

    handleKeydown(event) {
      const quickAdd = event.target.closest('quick-view-button.video-shopping__quick-add');
      if (quickAdd && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        quickAdd.click();
      }
    }

    togglePlayback(media) {
      const video = media?.querySelector('video');
      if (!video) return;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    }

    toggleVolume(media) {
      const video = media?.querySelector('video');
      const button = media?.querySelector('.video-shopping__volume');
      if (!video || !button) return;
      video.muted = !video.muted;
      button.setAttribute('aria-pressed', String(!video.muted));
      button.setAttribute('aria-label', video.muted ? this.dataset.unmuteLabel : this.dataset.muteLabel);
    }

    onPlay(activeVideo) {
      this.videos.forEach((video) => {
        if (video !== activeVideo) video.pause();
      });
      const media = activeVideo.closest('.video-shopping__media');
      media?.classList.add('is-playing');
      const button = media?.querySelector('.video-shopping__play');
      button?.setAttribute('aria-pressed', 'true');
      button?.setAttribute('aria-label', this.dataset.pauseLabel);
    }

    onPause(video) {
      const media = video.closest('.video-shopping__media');
      media?.classList.remove('is-playing');
      const button = media?.querySelector('.video-shopping__play');
      button?.setAttribute('aria-pressed', 'false');
      button?.setAttribute('aria-label', this.dataset.playLabel);
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) entry.target.pause();
      });
      if (this.dataset.autoplay !== 'true' || this.reducedMotion || this.autoplayStarted) return;
      const firstVisible = entries.find((entry) => entry.isIntersecting && entry.target.paused);
      if (firstVisible) {
        this.autoplayStarted = true;
        firstVisible.target.play().catch(() => {});
      }
    }
  });
}
